import { Box, DialogContent, Modal } from "@mui/material"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { ProgressSpinner } from "src/components/ProgressSpinner"
import { useModalDismiss } from "src/hooks/use-modal-dismiss"
import useSdk, {
  ErrorLevel,
  FacetType,
  SearchByFacetTypeQuery,
  SearchGlyphItem,
} from "src/hooks/use-sdk"
import useTranslate, { Namespaces } from "src/hooks/use-translate"
import { usePageviewTracking } from "src/screens/Detail/use-pageview-tracking"
import { TranslationKey } from "src/types"
import { comb, mouseHoverScroll } from "src/utilities/common"
import { theme, TI } from "src/utilities/theme"

import { DGText } from "../../../../components/common/DGText"
import { useEventBind } from "../../../../hooks/useEventBind"
import { s } from "../../../../utilities/common"
import {
  LGMouseEvent,
  Platform,
  platform,
} from "../../../../utilities/constants"
import { GetMetaInfo } from "../../../../utilities/use-focus-helper"
import { useRouteMatch } from "../../../../utilities/use-route-match"
import { CategoryScreenFocusKeys } from "../../../Category/category-focus-keys"

import { GlyphButton } from "./GlyphButton"
import { ResultList } from "./ResultList"

const titleTranslationKey: Record<
  FacetType,
  TranslationKey<Namespaces.Default>
> = {
  [FacetType.Composers]: "search__catalogue_composers",
  [FacetType.Conductors]: "search__catalogue_conductors",
  [FacetType.Ensembles]: "search__catalogue_ensembles",
  [FacetType.Soloists]: "search__catalogue_soloists",
  [FacetType.Venue]: "search__catalogue_venues",
}

export type TFacetBrowse = {
  open: comb<boolean>
  facetType?: FacetType
  getMetaInfo: GetMetaInfo<{ glyph: string; focusKey: string }>
}

const Screen = ({
  data,
  open,
  facetType,
  getMetaInfo,
}: {
  data: SearchByFacetTypeQuery
  open: comb<boolean>
  facetType?: FacetType
  getMetaInfo: GetMetaInfo<{ glyph: string; focusKey: string }>
}) => {
  const [sections, setSections] = useState<string[]>()
  const [selected, setSelected] = useState<string | undefined>()
  const [results, setResults] = useState<SearchGlyphItem[]>()

  useEffect(() => {
    const meta = getMetaInfo()
    if (meta && meta.glyph) {
      setFocus(CategoryScreenFocusKeys.GlyphButton + ":" + meta.glyph)
      if (meta && meta.glyph) {
        setFocus(CategoryScreenFocusKeys.GlyphButton + ":" + meta.glyph)
        if (meta.focusKey) setTimeout(() => setFocus(meta.focusKey), 20)
      }
      setSelected(meta.glyph)
    }
  }, [])

  const t = useTranslate()

  const track = usePageviewTracking()

  useEffect(() => {
    track("SearchFacet", `search/${facetType?.toLowerCase()}`)
  }, [])

  useEffect(() => {
    if (!selected) {
      const firstGlyph = data?.searchByFacetType?.sections[0]?.glyph
      if (firstGlyph) setSelected(firstGlyph)
    }
    setSections(
      data
        ? data.searchByFacetType?.sections.map((s) => s?.glyph ?? "")
        : undefined,
    )
  }, [data])

  useEffect(() => {
    if (data && data.searchByFacetType) {
      const tSection = data.searchByFacetType.sections
        .filter((s) => s && s.glyph === selected)[0]
        ?.items.filter((i): i is SearchGlyphItem => !!i)
      if (tSection) setResults(tSection)
    }
  }, [selected])

  const { ref, focusKey, focusSelf } = useFocusable({
    isFocusBoundary: true,
  })

  useEffect(() => {
    if (sections) focusSelf()
  }, [sections, focusSelf])

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        sx={{ display: "flex", flexDirection: "column" }}
        className={"loading_in"}
      >
        {facetType ? (
          <DGText
            tx={TI.H07tv}
            sx={{
              zIndex: 3,
              ml: s("96px"),
              mt: s("60px"),
              mb: s("48px"),
              color: "white",
              textTransform: "uppercase",
            }}
          >
            {t("search__results_title", [t(titleTranslationKey[facetType])])}
          </DGText>
        ) : null}
        <GlyphPicker
          selected={comb.ine(selected, setSelected)}
          sections={sections}
          open={open}
          facetType={facetType}
        />
        {facetType && selected ? (
          <ResultList
            results={results}
            facetType={facetType}
            selected={selected}
          />
        ) : null}
      </Box>
    </FocusContext.Provider>
  )
}

const GlyphPicker = ({
  selected,
  sections,
  open,
  facetType,
}: {
  selected: comb<string | undefined>
  sections?: string[]
  open: comb<boolean>
  facetType?: FacetType
}) => {
  const { ref, focusKey, hasFocusedChild, focusSelf } = useFocusable({
    saveLastFocusedChild: true,
    trackChildren: true,
  })
  useModalDismiss(
    () => {
      open.set(false)
      setFocus(CategoryScreenFocusKeys.FacetButton + ":" + facetType)
    },
    hasFocusedChild,
    true,
    () => focusSelf(),
  )

  useEffect(() => {
    // using the scroll ref ID for code simplicity
    const resultList = document.getElementById("glyphResultList")
    if (resultList) resultList.style.transform = "translate3d(0, 0, 0)"
  }, [selected.val])

  // DGAPP-6204 stop scrolling if mouse hides while scrolling
  const onStateChange = useCallback((e: LGMouseEvent) => {
    if (!isScrolling.current || e.detail.visibility) return
    isScrolling.current = undefined
  }, [])
  useEventBind(document, "cursorStateChange", onStateChange)

  const isScrolling = useRef<undefined | "left" | "right">(null)
  useEffect(() => {
    const scrollAmount = 200
    const interval = setInterval(
      () =>
        mouseHoverScroll({
          scrollAmount,
          scrollView: ref.current,
          direction: isScrolling.current,
          offset: s(537),
        }),
      250,
    )
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [])

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        sx={{ width: "min-content", overflowY: "visible" }}
        onMouseMove={(e) =>
          (isScrolling.current =
            e.clientX < s(50)
              ? "left"
              : e.clientX > s(1920 - 50)
                ? "right"
                : undefined)
        }
        onMouseLeave={() => (isScrolling.current = undefined)}
      >
        <Box
          sx={{
            pointerEvents: "none",
            zIndex: 2,
            position: "fixed",
            top: 0,
            left: 0,
            height: "100%",
            width: s("207px"),
            background:
              "linear-gradient(90deg, #151F31 0.93%, rgba(21, 31, 49, 0.00) 99.44%)",
          }}
        />
        <Box
          sx={{
            pointerEvents: "none",
            zIndex: 2,
            position: "fixed",
            top: 0,
            right: 0,
            height: "100%",
            width: s("207px"),
            background:
              "linear-gradient(270deg, #151F31 0.93%, rgba(21, 31, 49, 0.00) 99.44%)",
          }}
        />
        <Box
          ref={ref}
          sx={{
            transition: "transform 0.2s linear",
            transform: "translate3d(537px, 0, 0)",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            mb: s("24px"),
            "& > :not(:last-child)": {
              mr: s("8px"),
            },
            //transition: "transform 0.2s ease-in-out"
          }}
        >
          {sections?.map((glyph) => (
            <GlyphButton
              key={"glyphbutton-" + glyph}
              glyph={glyph}
              selected={selected}
              scrollRef={ref}
            />
          ))}
        </Box>
      </Box>
    </FocusContext.Provider>
  )
}

export const FacetBrowse = ({ open, facetType, getMetaInfo }: TFacetBrowse) => {
  const sdk = useSdk(ErrorLevel.WithPrompt, () => {
    open.set(false)
  })
  const [data, setData] = useState<SearchByFacetTypeQuery>()

  const isMyRoute = useRouteMatch()

  /* LG has a weird unreproducible bug on WebOS 6, so this is the workaround (DGAPP-5304)
   This might have been fixed in the meantime by adding DialogContent to Modals, but
   we're keeping it in place because we can't test it on our devices.
   */
  const [visible, setVisible] = useState(true)
  useEffect(() => {
    if (platform === Platform.WebOS) setVisible(isMyRoute)
  }, [isMyRoute])

  useEffect(() => {
    if (facetType && open.val)
      sdk.searchByFacetType({ input: facetType }).then((res) => setData(res))
  }, [facetType, open.val])
  useEffect(() => {
    if (!open.val) setData(undefined)
  }, [open.val])
  return (
    <Modal
      id={"FacetBrowse"}
      key={"facebrowse"}
      open={open.val}
      sx={{
        zIndex: visible ? undefined : -1,
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.main,
      }}
      hideBackdrop
    >
      <DialogContent>
        {open.val ? (
          <>
            {data ? (
              <Screen
                data={data}
                open={open}
                getMetaInfo={getMetaInfo}
                facetType={facetType}
              />
            ) : (
              <ProgressSpinner />
            )}
          </>
        ) : null}
      </DialogContent>
    </Modal>
  )
}
